import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface CellChangePayload {
  tableId: string | number;
  rowId: number;
  colId: number;
  value: string | number | null;
}

export interface CellValue {
  value: string | number | null;
  isSubmitted: boolean;
}

export interface TableQuestionState {
  tables: Record<
    string,
    {
      cellValues: Record<string, CellValue>;
      attemptsLeft?: number;
      isSubmitted?: boolean;
      modifiedCells?: string[];
    }
  >;
}

const initialState: TableQuestionState = {
  tables: {},
};

const tableQuestionSlice = createSlice({
  name: "tableQuestion",
  initialState,
  reducers: {
    updateTableCell: (state, action: PayloadAction<CellChangePayload>) => {
      const { tableId, rowId, colId, value } = action.payload;
      const tableKey = String(tableId);

      if (!state.tables[tableKey]) {
        state.tables[tableKey] = { cellValues: {} };
      }

      const cellKey = `${rowId}_${colId}`;

      // Preserve isSubmitted flag if cell was already submitted
      const existingCell = state.tables[tableKey].cellValues[cellKey];
      const isSubmitted = existingCell?.isSubmitted || false;

      // Store as object with value and preserved isSubmitted flag
      state.tables[tableKey].cellValues[cellKey] = {
        value,
        isSubmitted,
      };
    },
    resetTable: (
      state,
      action: PayloadAction<{
        tableId: string | number;
        resetAttempts?: boolean;
        initialAttempts?: number;
      }>
    ) => {
      const { tableId, resetAttempts, initialAttempts } = action.payload;
      const tableKey = String(tableId);
      if (state.tables[tableKey]) {
        state.tables[tableKey].cellValues = {};
        state.tables[tableKey].isSubmitted = false;
        state.tables[tableKey].modifiedCells = [];
        if (resetAttempts && initialAttempts !== undefined) {
          state.tables[tableKey].attemptsLeft = initialAttempts;
        }
      }
    },
    initializeTable: (
      state,
      action: PayloadAction<{
        tableId: string | number;
        initialValues?: Record<string, CellValue>;
        attemptsLeft?: number;
      }>
    ) => {
      const { tableId, initialValues = {}, attemptsLeft } = action.payload;
      const tableKey = String(tableId);
      state.tables[tableKey] = {
        cellValues: state.tables[tableKey]?.cellValues || initialValues,
        attemptsLeft,
        isSubmitted: false,
        modifiedCells: [],
      };
    },
    updateTableState: (
      state,
      action: PayloadAction<{
        tableId: string | number;
        attemptsLeft?: number;
        isSubmitted?: boolean;
        modifiedCells?: string[];
      }>
    ) => {
      const { tableId, attemptsLeft, isSubmitted, modifiedCells } =
        action.payload;
      const tableKey = String(tableId);

      if (!state.tables[tableKey]) {
        state.tables[tableKey] = { cellValues: {} };
      }

      if (attemptsLeft !== undefined) {
        state.tables[tableKey].attemptsLeft = attemptsLeft;
      }
      if (isSubmitted !== undefined) {
        state.tables[tableKey].isSubmitted = isSubmitted;
      }
      if (modifiedCells !== undefined) {
        state.tables[tableKey].modifiedCells = modifiedCells;
      }
    },
    submitTableAnswers: (
      state,
      action: PayloadAction<{
        tableId: string | number;
      }>
    ) => {
      const { tableId } = action.payload;
      const tableKey = String(tableId);

      if (!state.tables[tableKey]) {
        state.tables[tableKey] = { cellValues: {} };
      }

      // Mark all existing cells as submitted
      Object.keys(state.tables[tableKey].cellValues).forEach((cellKey) => {
        state.tables[tableKey].cellValues[cellKey].isSubmitted = true;
      });

      // Mark table as submitted
      state.tables[tableKey].isSubmitted = true;
    },
  },
});

export const {
  updateTableCell,
  resetTable,
  initializeTable,
  updateTableState,
  submitTableAnswers,
} = tableQuestionSlice.actions;

export const tableQuestionReducer = tableQuestionSlice.reducer;

// Selectors
export const selectTableCellValue = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number,
  rowId: number,
  colId: number
): string | number | null | undefined => {
  const tableKey = String(tableId);
  const cellKey = `${rowId}_${colId}`;
  return state.tableQuestion.tables[tableKey]?.cellValues[cellKey]?.value;
};

export const selectTableValues = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): Record<string, string | number | null> | undefined => {
  const tableKey = String(tableId);
  const cellValues = state.tableQuestion?.tables[tableKey]?.cellValues;

  if (!cellValues) return undefined;

  // Convert CellValue objects to plain values for backward compatibility
  const plainValues: Record<string, string | number | null> = {};
  Object.keys(cellValues).forEach((key) => {
    plainValues[key] = cellValues[key].value;
  });

  return plainValues;
};

export const selectTableAttemptsLeft = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): number | undefined => {
  const tableKey = String(tableId);
  return state.tableQuestion?.tables[tableKey]?.attemptsLeft;
};

export const selectTableIsSubmitted = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): boolean | undefined => {
  const tableKey = String(tableId);
  return state.tableQuestion?.tables[tableKey]?.isSubmitted;
};

export const selectTableModifiedCells = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): string[] | undefined => {
  const tableKey = String(tableId);
  return state.tableQuestion?.tables[tableKey]?.modifiedCells;
};
